import { type MutableRefObject, useEffect, useMemo, useRef } from "react";

import {
	CONCEPT_ANIMATIONS,
	type CompatLabels,
	type ConceptAnimId,
	type CostCurveLabels,
	compat,
	costCurve,
	timeTravel,
} from "./concept-anims";
import { createGraphField, type GraphFieldHandle, type GraphFieldOptions } from "./graph-field";
import {
	type ColorRGB,
	getAccent2Rgb,
	getAccentRgb,
	getPrimaryRgb,
	prefersReducedMotion,
	rgba,
} from "./types";

export type { CompatLabels, ConceptAnimId, CostCurveLabels } from "./concept-anims";
export { CONCEPT_ANIMATIONS } from "./concept-anims";

const HERO_GREEN_RGB: ColorRGB = "52,211,153";

interface RevealEntry {
	el: Element;
	delay: number;
	played: boolean;
}

const REVEAL_DELAY_DEFAULT = 700;
const REVEAL_TRANSLATE_PX = 22;

function easeOutCubic(k: number): number {
	return 1 - (1 - k) ** 3;
}

function tweenIn(entry: RevealEntry, reduced: boolean): void {
	if (entry.played) return;
	entry.played = true;
	const el = entry.el as HTMLElement;
	if (reduced) {
		el.classList.add("in");
		el.style.opacity = "1";
		el.style.transform = "none";
		return;
	}
	const dur = entry.delay > 0 ? entry.delay : REVEAL_DELAY_DEFAULT;
	let s: number | null = null;
	el.style.transform = `translateY(${REVEAL_TRANSLATE_PX}px)`;
	const tick = (t: number): void => {
		if (s === null) s = t;
		const k = Math.min(1, (t - s) / dur);
		const e = easeOutCubic(k);
		el.style.opacity = String(Math.min(1, e + 0.0001));
		el.style.transform = `translateY(${(1 - e) * REVEAL_TRANSLATE_PX}px)`;
		if (k < 1) requestAnimationFrame(tick);
		else {
			el.classList.add("in");
			el.style.opacity = "";
			el.style.transform = "";
		}
	};
	requestAnimationFrame(tick);
}

function revealImmediately(el: HTMLElement): void {
	el.classList.add("in");
	el.style.opacity = "";
	el.style.transform = "";
}

function checkReveal(reduced: boolean, set: Set<Element>): void {
	const revealLine = window.innerHeight * 0.92;
	for (const el of set) {
		if (!(el instanceof HTMLElement)) continue;
		const r = el.getBoundingClientRect();
		const inView = r.top < revealLine && r.bottom > 0;
		const scrolledPast = r.bottom < 0;
		if (!inView && !scrolledPast) continue;

		set.delete(el);
		if (reduced || scrolledPast) {
			revealImmediately(el);
			continue;
		}
		const delayAttr = el.getAttribute("data-d");
		const delay = delayAttr ? Number(delayAttr) * 120 : 0;
		tweenIn({ el, delay, played: false }, reduced);
	}
}

/**
 * Reveals `.reveal` elements as they enter the viewport, with a stagger
 * controlled by the `data-d` attribute (in 120ms increments).
 */
function collectPendingReveal(): Set<Element> {
	const pending = new Set<Element>();
	for (const el of document.querySelectorAll(".reveal:not(.in)")) {
		pending.add(el);
	}
	return pending;
}

export function useScrollReveal(): void {
	useEffect(() => {
		if (typeof window === "undefined") return;
		const reduced = prefersReducedMotion();
		const pending = collectPendingReveal();
		const run = (): void => {
			for (const el of document.querySelectorAll(".reveal:not(.in)")) {
				pending.add(el);
			}
			checkReveal(reduced, pending);
		};
		run();
		window.addEventListener("scroll", run, { passive: true });
		window.addEventListener("resize", run);
		const delayedPasses = [120, 400, 900];
		const timers = delayedPasses.map((d) => setTimeout(run, d));
		return () => {
			window.removeEventListener("scroll", run);
			window.removeEventListener("resize", run);
			for (const t of timers) clearTimeout(t);
		};
	}, []);
}

interface HeroState {
	name: "sleeping" | "waking" | "active";
	hold: number;
	fill: number;
	word: string;
	note: string;
	pill: string;
}

export interface HeroConsoleStateCopy {
	word: string;
	note: string;
	pill: string;
}

export interface HeroConsoleLabels {
	sleeping: HeroConsoleStateCopy;
	waking: HeroConsoleStateCopy;
	active: HeroConsoleStateCopy;
}

const HERO_TIMING: Array<Pick<HeroState, "name" | "hold" | "fill">> = [
	{ name: "sleeping", hold: 3200, fill: 4 },
	{ name: "waking", hold: 1100, fill: 50 },
	{ name: "active", hold: 3600, fill: 90 },
];

function buildHeroStates(labels: HeroConsoleLabels): HeroState[] {
	return HERO_TIMING.map((timing) => ({
		...timing,
		...labels[timing.name],
	}));
}

/**
 * Cycles the hero console through sleeping → waking → active states and
 * triggers a burst on the console canvas when active.
 */
export function useHeroStateMachine(burst?: (n: number) => void, labels?: HeroConsoleLabels): void {
	useEffect(() => {
		if (!labels) return;
		const heroStates = buildHeroStates(labels);
		const status = document.querySelector<HTMLElement>(".status");
		const fill = document.querySelector<HTMLElement>(".meter .fill");
		const cores = document.querySelector<HTMLElement>(".meter .cores");
		const note = document.querySelector<HTMLElement>(".cnote .note");
		if (!status) return;
		const txt = status.querySelector<HTMLElement>(".txt");

		const applyStatic = (s: HeroState): void => {
			status.dataset.state = s.name;
			if (txt) txt.textContent = s.pill;
			if (fill) fill.style.width = `${s.fill}%`;
			if (cores) cores.textContent = s.word;
			if (note) note.textContent = s.note;
		};

		if (prefersReducedMotion()) {
			applyStatic(heroStates[0] as HeroState);
			return;
		}

		let i = 0;
		let timer: ReturnType<typeof setTimeout> | null = null;
		const apply = (): void => {
			const s = heroStates[i] as HeroState;
			applyStatic(s);
			if (s.name === "active" && burst) burst(8);
			i = (i + 1) % heroStates.length;
			timer = setTimeout(apply, s.hold);
		};
		apply();
		return () => {
			if (timer) clearTimeout(timer);
		};
	}, [burst, labels]);
}

interface Particle {
	x: number;
	y: number;
	vy: number;
	down: boolean;
	life: number;
	col: string;
}

function storageFlowDraw(
	ctx: CanvasRenderingContext2D,
	W: number,
	H: number,
	t: number,
	parts: Particle[],
	lanes: number[],
): void {
	ctx.clearRect(0, 0, W, H);
	for (const p of parts) {
		p.y += p.vy * 1.6;
		p.life++;
		const trail = 26;
		const g = ctx.createLinearGradient(p.x, p.y, p.x, p.y - Math.sign(p.vy) * trail);
		g.addColorStop(0, rgba(p.col, 0));
		g.addColorStop(1, rgba(p.col, 0.55));
		ctx.strokeStyle = g;
		ctx.lineWidth = 1.5;
		ctx.beginPath();
		ctx.moveTo(p.x, p.y);
		ctx.lineTo(p.x, p.y - Math.sign(p.vy) * trail);
		ctx.stroke();
		ctx.fillStyle = rgba(p.col, 0.95);
		ctx.beginPath();
		ctx.arc(p.x, p.y, 1.8, 0, TAU);
		ctx.fill();
	}
	for (let i = parts.length - 1; i >= 0; i--) {
		const p = parts[i] as (typeof parts)[number];
		if (p.y < -40 || p.y > H + 40) parts.splice(i, 1);
	}
	if (Math.random() < 0.16 && parts.length < 60) {
		const x = lanes[Math.floor(Math.random() * lanes.length)] ?? W / 2;
		const down = Math.random() < 0.5;
		parts.push({
			x: x + (Math.random() - 0.5) * 24,
			y: down ? -10 : H + 10,
			vy: (down ? 1 : -1) * (0.9 + Math.random() * 0.8),
			down,
			life: 0,
			col: down ? getAccentRgb() : getAccent2Rgb(),
		});
	}
	void t;
}

const TAU = Math.PI * 2;

function drawStorageDot(
	ctx: CanvasRenderingContext2D,
	x: number,
	y: number,
	r: number,
	col: string,
	glow: boolean,
): void {
	if (glow) {
		const g = ctx.createRadialGradient(x, y, 0, x, y, r * 4);
		g.addColorStop(0, rgba(col, 0.5));
		g.addColorStop(1, rgba(col, 0));
		ctx.fillStyle = g;
		ctx.beginPath();
		ctx.arc(x, y, r * 4, 0, TAU);
		ctx.fill();
	}
	ctx.fillStyle = rgba(col, 1);
	ctx.beginPath();
	ctx.arc(x, y, r, 0, TAU);
	ctx.fill();
}

export interface BranchDemoLabels {
	main: string;
	branchLabel: string;
}

function branchDemoDraw(
	ctx: CanvasRenderingContext2D,
	W: number,
	H: number,
	t: number,
	labels: BranchDemoLabels,
): void {
	const DUR = 5200;
	const p = (t % DUR) / DUR;
	ctx.clearRect(0, 0, W, H);
	const ml = H * 0.66;
	const x0 = W * 0.08;
	const x1 = W * 0.92;
	const forkX = x0 + (x1 - x0) * 0.42;
	const bl = H * 0.3;
	const accent = getAccentRgb();
	const accent2 = getAccent2Rgb();

	ctx.strokeStyle = "rgba(255,255,255,0.16)";
	ctx.lineWidth = 2;
	ctx.beginPath();
	ctx.moveTo(x0, ml);
	ctx.lineTo(x1, ml);
	ctx.stroke();

	const mp = Math.min(1, p / 0.42);
	ctx.strokeStyle = rgba(accent, 0.9);
	ctx.lineWidth = 2;
	ctx.beginPath();
	ctx.moveTo(x0, ml);
	ctx.lineTo(x0 + (x1 - x0) * 0.42 * mp, ml);
	ctx.stroke();

	const bp = Math.max(0, Math.min(1, (p - 0.42) / 0.5));
	if (bp > 0) {
		const bx1 = forkX + (x1 - forkX) * 0.95;
		ctx.strokeStyle = rgba(accent2, 0.95);
		ctx.lineWidth = 2;
		ctx.beginPath();
		ctx.moveTo(forkX, ml);
		const cx = forkX + 26;
		ctx.quadraticCurveTo(cx, ml, forkX + 34, bl);
		const ex = forkX + 34 + (bx1 - forkX - 34) * bp;
		ctx.lineTo(ex, bl);
		ctx.stroke();
		drawStorageDot(ctx, ex, bl, 3.5, accent2, true);
	}

	for (let i = 0; i <= 4; i++) {
		const x = x0 + (x1 - x0) * (i / 4) * 0.96;
		const passed = x <= x0 + (x1 - x0) * 0.42 * mp;
		drawStorageDot(ctx, x, ml, 3, passed ? accent : "120,130,128", false);
	}
	drawStorageDot(ctx, forkX, ml, 4, accent, p > 0.42);

	ctx.font = "11px JetBrains Mono, monospace";
	ctx.fillStyle = "rgba(150,160,158,0.9)";
	ctx.fillText(labels.main, x0, ml + 20);
	if (bp > 0) {
		ctx.fillStyle = rgba(accent2, 0.95);
		ctx.fillText(labels.branchLabel, forkX + 36, bl - 10);
	}
}

/**
 * Find a canvas by id and mount a GraphField instance. Returns a stable
 * `burst` handle (no-op until the canvas mounts). Re-runs when `id` changes.
 */
export function useGraphFieldById(
	id: string,
	options: GraphFieldOptions = {},
): { burst: (n?: number) => void } {
	const instanceRef = useRef<GraphFieldHandle | null>(null);
	const optsKey = useMemo(
		() =>
			JSON.stringify({
				count: options.count ?? null,
				linkDist: options.linkDist ?? null,
				speed: options.speed ?? null,
				nodeR: options.nodeR ?? null,
				pulseRate: options.pulseRate ?? null,
				accentRGB: options.accentRGB ?? null,
				acc2RGB: options.acc2RGB ?? null,
				dim: options.dim ?? null,
			}),
		[
			options.count,
			options.linkDist,
			options.speed,
			options.nodeR,
			options.pulseRate,
			options.accentRGB,
			options.acc2RGB,
			options.dim,
		],
	);

	const optionsRef = useRef(options);
	optionsRef.current = options;

	useEffect(() => {
		const canvas = document.getElementById(id);
		if (!(canvas instanceof HTMLCanvasElement)) return;
		const handle = createGraphField(canvas, optionsRef.current);
		instanceRef.current = handle;
		return () => {
			handle.destroy();
			instanceRef.current = null;
		};
	}, [id, optsKey]);

	return {
		burst: (n?: number) => instanceRef.current?.burst(n),
	};
}

/**
 * Find a canvas by id and mount a concept-anim factory (cost curve, scale
 * zero, time travel, etc.). Re-runs when `id` changes.
 */
export function useConceptAnimById(id: ConceptAnimId, labels?: string[] | CompatLabels): void {
	useEffect(() => {
		const canvas = document.getElementById(id);
		if (!(canvas instanceof HTMLCanvasElement)) return;
		const draw =
			id === "anim-timetravel"
				? timeTravel(labels as string[] | undefined)
				: id === "anim-compat"
					? compat(labels as CompatLabels | undefined)
					: CONCEPT_ANIMATIONS[id]();
		let rafId: number | null = null;
		let running = true;
		let width = 0;
		let height = 0;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;
		const reduced = prefersReducedMotion();
		const t0 = performance.now();

		const size = (): void => {
			const rect = canvas.getBoundingClientRect();
			width = Math.max(1, rect.width);
			height = Math.max(1, rect.height);
			const dpr = Math.min(window.devicePixelRatio || 1, 2);
			canvas.width = width * dpr;
			canvas.height = height * dpr;
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
		};

		const frame = (now: number): void => {
			if (!running) return;
			draw(ctx, width, height, now - t0);
			rafId = requestAnimationFrame(frame);
		};

		const onResize = (): void => {
			size();
		};

		const io = new IntersectionObserver(
			(entries) => {
				for (const e of entries) {
					running = e.isIntersecting;
					if (running && !reduced) {
						rafId = requestAnimationFrame(frame);
					} else if (rafId !== null) {
						cancelAnimationFrame(rafId);
						rafId = null;
					}
				}
			},
			{ threshold: 0 },
		);

		size();
		if (reduced) draw(ctx, width, height, 3000);
		else rafId = requestAnimationFrame(frame);
		io.observe(canvas);
		window.addEventListener("resize", onResize, { passive: true });

		return () => {
			if (rafId !== null) cancelAnimationFrame(rafId);
			io.disconnect();
			window.removeEventListener("resize", onResize);
		};
	}, [id, labels]);
}

/**
 * Find the cost-curve canvas by id and mount the animation with
 * localised labels.
 */
export function useCostCurveAnim(id: string, labels: CostCurveLabels): void {
	useEffect(() => {
		const canvas = document.getElementById(id);
		if (!(canvas instanceof HTMLCanvasElement)) return;
		const draw = costCurve(labels);
		let rafId: number | null = null;
		let running = true;
		let width = 0;
		let height = 0;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;
		const reduced = prefersReducedMotion();
		const t0 = performance.now();

		const size = (): void => {
			const rect = canvas.getBoundingClientRect();
			width = Math.max(1, rect.width);
			height = Math.max(1, rect.height);
			const dpr = Math.min(window.devicePixelRatio || 1, 2);
			canvas.width = width * dpr;
			canvas.height = height * dpr;
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
		};

		const frame = (now: number): void => {
			if (!running) return;
			draw(ctx, width, height, now - t0);
			rafId = requestAnimationFrame(frame);
		};

		const onResize = (): void => {
			size();
		};

		const io = new IntersectionObserver(
			(entries) => {
				for (const e of entries) {
					running = e.isIntersecting;
					if (running && !reduced) {
						rafId = requestAnimationFrame(frame);
					} else if (rafId !== null) {
						cancelAnimationFrame(rafId);
						rafId = null;
					}
				}
			},
			{ threshold: 0 },
		);

		size();
		if (reduced) draw(ctx, width, height, 3000);
		else rafId = requestAnimationFrame(frame);
		io.observe(canvas);
		window.addEventListener("resize", onResize, { passive: true });

		return () => {
			if (rafId !== null) cancelAnimationFrame(rafId);
			io.disconnect();
			window.removeEventListener("resize", onResize);
		};
	}, [id, labels]);
}

/**
 * Find the storage-flow canvas by id and wire the particles / lanes
 * animation.
 */
export function useStorageFlowById(id: string): void {
	const parts = useRef<Particle[]>([]);
	const lanes = useRef<number[]>([]);
	const canvasRef = useRef<HTMLCanvasElement | null>(null);

	useEffect(() => {
		const el = document.getElementById(id);
		canvasRef.current = el instanceof HTMLCanvasElement ? el : null;
		if (!canvasRef.current) return;
		const computeLanes = (W: number): void => {
			const n = Math.max(4, Math.round(W / 150));
			lanes.current = Array.from({ length: n }, (_, i) => (i + 0.5) * (W / n));
		};
		const onResize = (): void => {
			const W = canvasRef.current?.getBoundingClientRect().width ?? 0;
			computeLanes(W);
		};
		onResize();
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, [id]);

	useEffect(() => {
		if (!canvasRef.current) return;
		const handle = createStorageFlowHandle(
			canvasRef.current,
			parts as MutableRefObject<Particle[]>,
			lanes as MutableRefObject<number[]>,
		);
		return handle.destroy;
	}, [id]);
}

/**
 * Find the branch-demo canvas by id and wire the timeline drawing.
 */
export function useBranchDemoById(id: string, labels: BranchDemoLabels): void {
	const canvasRef = useRef<HTMLCanvasElement | null>(null);

	useEffect(() => {
		const el = document.getElementById(id);
		canvasRef.current = el instanceof HTMLCanvasElement ? el : null;
	}, [id]);

	useEffect(() => {
		if (!canvasRef.current) return;
		const handle = createBranchDemoHandle(canvasRef.current, labels);
		return handle.destroy;
	}, [id, labels]);
}

function createStorageFlowHandle(
	canvas: HTMLCanvasElement,
	parts: MutableRefObject<Particle[]>,
	lanes: MutableRefObject<number[]>,
): { destroy: () => void } {
	let width = 0;
	let height = 0;
	let rafId: number | null = null;
	let running = true;
	let resizeTimer: ReturnType<typeof setTimeout> | null = null;
	const ctx = canvas.getContext("2d");
	if (!ctx) return { destroy: () => undefined };
	const reduced = prefersReducedMotion();
	const t0 = performance.now();

	const size = (): void => {
		const r = canvas.getBoundingClientRect();
		width = Math.max(1, r.width);
		height = Math.max(1, r.height);
		const dpr = Math.min(window.devicePixelRatio || 1, 2);
		canvas.width = width * dpr;
		canvas.height = height * dpr;
		ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
	};

	const draw = (t: number): void => {
		storageFlowDraw(ctx, width, height, t, parts.current ?? [], lanes.current ?? []);
	};

	const frame = (now: number): void => {
		if (!running) return;
		draw(now - t0);
		rafId = requestAnimationFrame(frame);
	};

	const onResize = (): void => {
		if (resizeTimer) clearTimeout(resizeTimer);
		resizeTimer = setTimeout(size, 180);
	};

	const io = new IntersectionObserver(
		(entries) => {
			for (const e of entries) {
				running = e.isIntersecting;
				if (running && !reduced) rafId = requestAnimationFrame(frame);
				else if (rafId !== null) {
					cancelAnimationFrame(rafId);
					rafId = null;
				}
			}
		},
		{ threshold: 0 },
	);

	size();
	for (let i = 0; i < 14; i++) {
		parts.current ??= [];
		lanes.current ??= [];
		if (lanes.current.length === 0) {
			const n = Math.max(4, Math.round(width / 150));
			lanes.current = Array.from({ length: n }, (_, i2) => (i2 + 0.5) * (width / n));
		}
		const x = lanes.current[Math.floor(Math.random() * lanes.current.length)] ?? width / 2;
		const down = Math.random() < 0.5;
		parts.current.push({
			x: x + (Math.random() - 0.5) * 24,
			y: down ? -10 : height + 10,
			vy: (down ? 1 : -1) * (0.9 + Math.random() * 0.8),
			down,
			life: 0,
			col: down ? getAccentRgb() : getAccent2Rgb(),
		});
		const last = parts.current[parts.current.length - 1] as (typeof parts.current)[number];
		last.y = Math.random() * height;
	}
	if (reduced) return { destroy: () => undefined };
	rafId = requestAnimationFrame(frame);
	io.observe(canvas);
	window.addEventListener("resize", onResize, { passive: true });

	return {
		destroy: () => {
			if (rafId !== null) cancelAnimationFrame(rafId);
			if (resizeTimer) clearTimeout(resizeTimer);
			io.disconnect();
			window.removeEventListener("resize", onResize);
		},
	};
}

function createBranchDemoHandle(
	canvas: HTMLCanvasElement,
	labels: BranchDemoLabels,
): {
	destroy: () => void;
} {
	let width = 0;
	let height = 0;
	let rafId: number | null = null;
	let running = true;
	let resizeTimer: ReturnType<typeof setTimeout> | null = null;
	const ctx = canvas.getContext("2d");
	if (!ctx) return { destroy: () => undefined };
	const reduced = prefersReducedMotion();
	const t0 = performance.now();

	const size = (): void => {
		const r = canvas.getBoundingClientRect();
		width = Math.max(1, r.width);
		height = Math.max(1, r.height);
		const dpr = Math.min(window.devicePixelRatio || 1, 2);
		canvas.width = width * dpr;
		canvas.height = height * dpr;
		ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
	};

	const draw = (t: number): void => {
		branchDemoDraw(ctx, width, height, t, labels);
	};

	const frame = (now: number): void => {
		if (!running) return;
		draw(now - t0);
		rafId = requestAnimationFrame(frame);
	};

	const onResize = (): void => {
		if (resizeTimer) clearTimeout(resizeTimer);
		resizeTimer = setTimeout(size, 180);
	};

	const io = new IntersectionObserver(
		(entries) => {
			for (const e of entries) {
				running = e.isIntersecting;
				if (running && !reduced) {
					rafId = requestAnimationFrame(frame);
				} else if (rafId !== null) {
					cancelAnimationFrame(rafId);
					rafId = null;
				}
			}
		},
		{ threshold: 0 },
	);

	size();
	if (reduced) {
		draw(5200 * 0.7);
		return { destroy: () => undefined };
	}
	rafId = requestAnimationFrame(frame);
	io.observe(canvas);
	window.addEventListener("resize", onResize, { passive: true });

	return {
		destroy: () => {
			if (rafId !== null) cancelAnimationFrame(rafId);
			if (resizeTimer) clearTimeout(resizeTimer);
			io.disconnect();
			window.removeEventListener("resize", onResize);
		},
	};
}

void getPrimaryRgb;
void HERO_GREEN_RGB;
